/**
 * Centralized error handling utility for consistent error management
 */

export interface ErrorResult {
	message: string;
	type: 'error' | 'warning' | 'info';
	details?: string[];
}

/**
 * Handles API errors and converts them to user-friendly messages
 */
export async function handleApiError(response: Response): Promise<ErrorResult> {
	try {
		const errorData = await response.json();
		
		// Handle structured error responses
		if (errorData.error) {
			return {
				message: errorData.error,
				type: 'error',
				details: errorData.details || undefined
			};
		}
		
		// Handle validation errors
		if (errorData.errors && Array.isArray(errorData.errors)) {
			return {
				message: 'Er zijn validatiefouten opgetreden',
				type: 'error',
				details: errorData.errors
			};
		}
		
		// Fallback for unknown structure
		return {
			message: errorData.message || `Server fout: ${response.status} ${response.statusText}`,
			type: 'error'
		};
		
	} catch (parseError) {
		// If we can't parse the JSON, show a generic error
		return {
			message: `Server fout: ${response.status} ${response.statusText}`,
			type: 'error'
		};
	}
}

/**
 * Handles network and other runtime errors
 */
export function handleNetworkError(error: Error): ErrorResult {
	if (error.name === 'TypeError' && error.message.includes('fetch')) {
		return {
			message: 'Kan geen verbinding maken met de server. Controleer je internetverbinding.',
			type: 'error'
		};
	}
	
	if (error.name === 'AbortError') {
		return {
			message: 'Verzoek werd geannuleerd',
			type: 'warning'
		};
	}
	
	return {
		message: error.message || 'Er is een onbekende fout opgetreden',
		type: 'error'
	};
}

/**
 * Validates form input and returns validation errors
 */
export function validateInput(value: string, rules: {
	required?: boolean;
	minLength?: number;
	maxLength?: number;
	pattern?: RegExp;
	custom?: (value: string) => string | null;
}): string | null {
	if (rules.required && (!value || value.trim() === '')) {
		return 'Dit veld is verplicht';
	}
	
	if (value && rules.minLength && value.length < rules.minLength) {
		return `Minimaal ${rules.minLength} karakters vereist`;
	}
	
	if (value && rules.maxLength && value.length > rules.maxLength) {
		return `Maximaal ${rules.maxLength} karakters toegestaan`;
	}
	
	if (value && rules.pattern && !rules.pattern.test(value)) {
		return 'Ongeldige indeling';
	}
	
	if (value && rules.custom) {
		return rules.custom(value);
	}
	
	return null;
}

/**
 * Debounce function to limit API calls
 */
export function debounce<T extends (...args: any[]) => void>(
	func: T,
	delay: number
): (...args: Parameters<T>) => void {
	let timeoutId: ReturnType<typeof setTimeout>;
	
	return (...args: Parameters<T>) => {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => func(...args), delay);
	};
}

/**
 * Creates a retry function for failed API calls
 */
export function withRetry<T>(
	fn: () => Promise<T>,
	maxRetries = 3,
	delay = 1000
): Promise<T> {
	return new Promise(async (resolve, reject) => {
		let attempts = 0;
		
		const attempt = async () => {
			try {
				const result = await fn();
				resolve(result);
			} catch (error) {
				attempts++;
				
				if (attempts >= maxRetries) {
					reject(error);
					return;
				}
				
				// Exponential backoff
				const retryDelay = delay * Math.pow(2, attempts - 1);
				setTimeout(attempt, retryDelay);
			}
		};
		
		attempt();
	});
}